import { LoginProviderType } from '@common/services/auth/auth.service.interface';
import { useTranslation } from '@input-web/configs/locale/locale.config';
import standardStore, { useStandardStore } from '@input-web/modules/standard/stores/standard.store';
import standardDetailLayoutRoute from '@input-web/modules/standard/views/detail-layout/standard-detail-layout.route';
import standardListRoute from '@input-web/modules/standard/views/list/standard-list.route';
import { UseMutateFunction } from '@tanstack/react-query';
import { useLocation, useMatch, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';

/**
 * Hook for handling navbar interactions and state in the assignment list view
 * Manages subject group selection and page title display
 */
export default function useStandardLayoutNavbarHandler(
  onLogout: UseMutateFunction<void, unknown, LoginProviderType | undefined>,
) {
  // #region Dependencies
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isDetailLayoutMatched = !!useMatch({
    from: standardDetailLayoutRoute.id,
    shouldThrow: false,
  });
  const { shouldConfirmLeavingForm } = useStandardStore();
  // #endregion

  // #region Computed Values
  const pageTitleRef = useRef<string>('');
  const title = useMemo(() => {
    if (location.pathname === standardListRoute.fullPath) return t('standard.list.page-title');
    if (isDetailLayoutMatched) return t('standard.detail-layout.page-title');

    return pageTitleRef.current;
  }, [t, location.pathname, isDetailLayoutMatched]);
  pageTitleRef.current = title;
  const actionLabel = useMemo(() => {
    if (isDetailLayoutMatched) return t('standard.layout.navbar.back');

    return t('standard.layout.navbar.logout');
  }, [t, isDetailLayoutMatched]);
  const onActionClick = useMemo(() => {
    if (isDetailLayoutMatched) {
      const onBack = () => {
        navigate({
          search: (current) => ({ ...current, questionId: undefined }),
          to: standardListRoute.fullPath,
        });
      };

      if (shouldConfirmLeavingForm) {
        return () => standardStore.actions.openConfirmationDialog({ onConfirm: onBack });
      }

      return onBack;
    }

    return () => onLogout(undefined);
  }, [isDetailLayoutMatched, navigate, onLogout, shouldConfirmLeavingForm]);
  // #endregion

  return { isDetailLayoutMatched, props: { actionLabel, onActionClick, title } };
}
